import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useCustomers } from '@/hooks/useCustomers';

const segmentColors: Record<string, string> = {
  'FİZBO': 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  'Yatırımcı': 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-400',
  'Portföy Sahibi': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

function AddCustomerModal({
  isOpen,
  onClose,
  onSubmit,
  groups,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; phone: string; email: string; segment: string; group: string }) => void;
  groups: { name: string }[];
}) {
  const { isDark } = useTheme();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [segment, setSegment] = useState('FİZBO');
  const [group, setGroup] = useState(groups[0]?.name || 'Genel');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!name.trim() || !phone.trim()) {
      setError('Ad ve telefon zorunludur.');
      return;
    }
    onSubmit({ name: name.trim(), phone: phone.trim(), email: email.trim(), segment, group });
    setName('');
    setPhone('');
    setEmail('');
    setSegment('FİZBO');
    setGroup(groups[0]?.name || 'Genel');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-base font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Yeni Müşteri</h3>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-full ${isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'}`}>
            <i className="ri-close-line" />
          </button>
        </div>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`} />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon (örn: 0532 123 45 67)" className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posta (isteğe bağlı)" type="email" className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`} />
          <div className="grid grid-cols-2 gap-3">
            <select value={segment} onChange={(e) => setSegment(e.target.value)} className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'}`}>
              <option value="FİZBO">FİZBO</option>
              <option value="Yatırımcı">Yatırımcı</option>
              <option value="Portföy Sahibi">Portföy Sahibi</option>
            </select>
            <select value={group} onChange={(e) => setGroup(e.target.value)} className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'}`}>
              {groups.map((g) => (
                <option key={g.name} value={g.name}>{g.name}</option>
              ))}
              <option value="Genel">Genel</option>
            </select>
          </div>
        </div>
        <button onClick={handleSubmit} className="mt-4 w-full py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors">
          Kaydet
        </button>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  isOpen,
  name,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { isDark } = useTheme();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-sm rounded-2xl p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <p className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          <strong>{name}</strong> silinsin mi?
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Bu işlem geri alınamaz.</p>
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className={`flex-1 py-2.5 text-sm font-medium rounded-lg border ${isDark ? 'border-dark-border text-dark-text hover:bg-dark-border' : 'border-light-border text-light-text hover:bg-light-bg'}`}>İptal</button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors">Sil</button>
        </div>
      </div>
    </div>
  );
}

export default function MusteriRehberPage() {
  const { isDark } = useTheme();
  const { customers, groups, isLoading, addCustomer, deleteCustomer } = useCustomers();
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('Tümü');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const filteredCustomers = customers.filter((item) => {
    const matchesSearch =
      !search ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.phone.includes(search);
    const matchesSegment = segmentFilter === 'Tümü' || item.segment === segmentFilter;
    const matchesGroup = !selectedGroup || item.group === selectedGroup;
    return matchesSearch && matchesSegment && matchesGroup;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-500">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-user-line text-white text-lg" />
            </div>
          </div>
          <div>
            <h1 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Müşteri & Rehber
            </h1>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {customers.length} kayıtlı müşteri
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="w-full sm:w-auto py-2.5 px-4 bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line" />
          </div>
          Yeni Müşteri
        </button>
      </div>

      {/* Groups */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setSelectedGroup(null)}
          className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            !selectedGroup
              ? 'bg-primary-500 text-white'
              : isDark
              ? 'bg-dark-card border border-dark-border text-dark-muted hover:bg-dark-border'
              : 'bg-white border border-light-border text-light-muted hover:bg-light-bg'
          }`}
        >
          Tümü ({customers.length})
        </button>
        {groups.map((group) => (
          <button
            key={group.id}
            onClick={() => setSelectedGroup(group.name)}
            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedGroup === group.name
                ? 'bg-primary-500 text-white'
                : isDark
                ? 'bg-dark-card border border-dark-border text-dark-muted hover:bg-dark-border'
                : 'bg-white border border-light-border text-light-muted hover:bg-light-bg'
            }`}
          >
            {group.name} ({group.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className={`rounded-xl p-3 md:p-4 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 ${
            isDark ? 'bg-dark-bg border-dark-border' : 'bg-light-bg border-light-border'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-search-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Müşteri ara..."
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
              }`}
            />
          </div>
          <select
            value={segmentFilter}
            onChange={(e) => setSegmentFilter(e.target.value)}
            className={`px-3 py-2 rounded-lg text-sm border outline-none ${
              isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'
            }`}
          >
            <option value="Tümü">Tüm Segmentler</option>
            <option value="FİZBO">FİZBO</option>
            <option value="Yatırımcı">Yatırımcı</option>
            <option value="Portföy Sahibi">Portföy Sahibi</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className="divide-y dark:divide-dark-border divide-light-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customer List */}
      {!isLoading && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-xs font-medium ${isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'}`}>
                  <th className="px-4 py-3">Ad Soyad</th>
                  <th className="px-4 py-3">Telefon</th>
                  <th className="px-4 py-3">Segment</th>
                  <th className="px-4 py-3">Etiketler</th>
                  <th className="px-4 py-3">Grup</th>
                  <th className="px-4 py-3">Son İletişim</th>
                  <th className="px-4 py-3">İşlem</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className={`text-sm ${isDark ? 'divide-y divide-dark-border' : 'divide-y divide-light-border'}`}>
                {filteredCustomers.map((item) => (
                  <tr
                    key={item.id}
                    className={`transition-colors ${isDark ? 'hover:bg-dark-border/50' : 'hover:bg-light-bg'}`}
                  >
                    <td className={`px-4 py-3 font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.name}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.phone}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${segmentColors[item.segment]}`}>
                        {item.segment}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {item.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.group}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.lastContact}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.totalDeals}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <a
                          href={`tel:${item.phone.replace(/\s/g, '')}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-500 hover:bg-primary-200 transition-colors"
                        >
                          <i className="ri-phone-line" />
                        </a>
                        <button
                          onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20 text-red-500 hover:bg-red-200 transition-colors"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y dark:divide-dark-border divide-light-border">
            {filteredCustomers.map((item) => (
              <div key={item.id} className={`p-4 ${isDark ? 'bg-dark-card' : 'bg-white'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.name}
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.phone}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${segmentColors[item.segment]}`}>
                    {item.segment}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {item.group} · {item.totalDeals} işlem
                  </span>
                  <div className="flex items-center gap-2">
                    <a
                      href={`tel:${item.phone.replace(/\s/g, '')}`}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-500"
                    >
                      <i className="ri-phone-line" />
                    </a>
                    <button
                      onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20 text-red-500"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredCustomers.length === 0 && (
        <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-dark-border flex items-center justify-center">
            <i className="ri-user-search-line text-2xl text-gray-400 dark:text-dark-muted" />
          </div>
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Müşteri bulunamadı
          </p>
        </div>
      )}

      {/* Modals */}
      <AddCustomerModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={(data) => addCustomer(data)}
        groups={groups}
      />
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        name={deleteTarget?.name || ''}
        onConfirm={() => {
          if (deleteTarget) {
            deleteCustomer(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
